import { has, isNil } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { getPrimaryMetricFromCard } from "@radarly/models/cards/metric";
import {
  getExcludedFocusesFromClause,
  getFocusesFromClause,
} from "@radarly/shared/cards/facets";
import { getClauseFromCard } from "../clause/getter";
import {
  isAIDataviz,
  isCloudDataviz,
  isEmbeddedDataviz,
  isEmvDataviz,
  isHorizontalBarChartDataviz,
  isImageClusteringDataviz,
  isPinnedAuthorDataviz,
  isPinnedPostDataviz,
  isTextDataviz,
  isTimeUnitDataviz,
} from "../dataviz";
import { Query } from "../queries";
import {
  GlobalFragment,
  getTermFromAggs,
  isAIFragment,
  isEmptyFragment,
  isInfluencerFragment,
  isSearchFragmentClassification,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
  isSearchFragmentDemographic,
  isSearchFragmentGeo,
  isSearchFragmentPivot,
  isSearchFragmentPosts,
  isSearchFragmentTerm,
  isSearchFragmentUsers,
} from "../search";
import {
  AICard,
  Card,
  ClassificationCard,
  ClusterCard,
  DateMetricCard,
  DateMetricsCard,
  DateTermCard,
  DemographicCard,
  EmbeddedCard,
  EmvCard,
  GeoCard,
  HiddenTermCard,
  PinnedAuthorCard,
  PinnedPostCard,
  PivotCard,
  PostsCard,
  TermCard,
  TermMetricsCard,
  TextCard,
  TimeUnitCard,
  UsersCard,
} from "./model";
import { getTermFromCard, hideableTerms } from "./term";

export function isTermMetricsCard(card: Card): card is TermMetricsCard {
  const fragment = card.fragments[0];
  if (fragment && isSearchFragmentTerm(fragment)) {
    const term = getTermFromAggs(fragment.request.aggs);
    return fragment.request.aggs[term].metrics.length > 1;
  } else {
    return false;
  }
}

export function isGeoCard(card: Card): card is GeoCard {
  const fragment = card.fragments[0];
  return fragment && isSearchFragmentGeo(fragment);
}

export function isAICard(card: Card): card is AICard {
  const fragment = card.fragments[0];
  return fragment && isAIFragment(fragment);
}

export function isDateMetricCard(card: Card): card is DateMetricCard {
  const fragment = card.fragments[0];
  if (fragment && isSearchFragmentDateMetrics(fragment)) {
    return fragment.request.aggs.date.metrics.length === 1;
  } else {
    return false;
  }
}

export function isDateMetricsCard(card: Card): card is DateMetricsCard {
  const fragment = card.fragments[0];
  if (fragment && isSearchFragmentDateMetrics(fragment)) {
    return fragment.request.aggs.date.metrics.length > 1;
  } else {
    return false;
  }
}

export function isDateTermCard(card: Card): card is DateTermCard {
  const fragment = card.fragments[0];
  return (
    fragment &&
    isSearchFragmentDateTerm(fragment) &&
    // @TO-DO: double-check here
    !isAIDataviz(card.dataviz) &&
    !isEmvDataviz(card.dataviz)
  );
}

export function isUsersCard(card: Card): card is UsersCard {
  const fragment = card.fragments[0];
  return fragment && isSearchFragmentUsers(fragment);
}

export function isPivotCard(card: Card): card is PivotCard {
  const fragment = card.fragments[0];
  return fragment && isSearchFragmentPivot(fragment);
}

export function isTermCard(card: Card): card is TermCard {
  const fragment = card.fragments[0];
  if (fragment && isSearchFragmentTerm(fragment)) {
    const term = getTermFromAggs(fragment.request.aggs);
    return fragment.request.aggs[term].metrics.length === 1;
  } else {
    return false;
  }
}

export function isDemographicCard(card: Card): card is DemographicCard {
  const fragment = card.fragments[0];
  return fragment && isSearchFragmentDemographic(fragment);
}

export function isPostsCard(card: Card): card is PostsCard {
  const fragment = card.fragments[0];
  return (
    fragment &&
    !isPinnedPostDataviz(card.dataviz) &&
    !isPinnedAuthorDataviz(card.dataviz) &&
    isSearchFragmentPosts(fragment) &&
    !isImageClusteringDataviz(card.dataviz) &&
    !isAIDataviz(card.dataviz)
  );
}

export function isClassificationCard(card: Card): card is ClassificationCard {
  const fragment = card.fragments[0];
  return fragment && isSearchFragmentClassification(fragment);
}

export function isEmbeddedCard(card: Card): card is EmbeddedCard {
  if (card.fragments.length === 0) {
    return isEmptyFragment(card.fragments) && isEmbeddedDataviz(card.dataviz);
  }
  return false;
}

export function isTextCard(card: Card): card is TextCard {
  if (card.fragments.length === 0) {
    return isEmptyFragment(card.fragments) && isTextDataviz(card.dataviz);
  }
  return false;
}

export function isPinnedPostCard(card: Card): card is PinnedPostCard {
  return isPinnedPostDataviz(card.dataviz);
}

export function isPinnedAuthorCard(card: Card): card is PinnedAuthorCard {
  return isPinnedAuthorDataviz(card.dataviz);
}

export function isEmvCard(card: Card): card is EmvCard {
  return isEmvDataviz(card.dataviz);
}

export function isClusterCard(card: Card): card is ClusterCard {
  return isImageClusteringDataviz(card.dataviz);
}

export function isHiddenTermsCard(card: Card): card is HiddenTermCard {
  const term = getTermFromCard(card);
  const canHideTerm = hideableTerms.includes(term as Facet);
  const isValidDataviz =
    isCloudDataviz(card.dataviz) || isHorizontalBarChartDataviz(card.dataviz);
  return isValidDataviz && canHideTerm;
}

export function isCardWithEvolutionSelector(card: Card): boolean {
  const metric = getPrimaryMetricFromCard(card);
  const isCardAvailable =
    isTermCard(card) ||
    isDateMetricCard(card) ||
    isGeoCard(card) ||
    isClassificationCard(card);
  //TODO:  add other card if necessary
  return isCardAvailable && metric !== Metric.Rating;
}

export function isCardWithNullValues(card: Card): boolean {
  return isTermCard(card); //TODO:  add other card if necessary
}

export function isCardSortable(card: Card): boolean {
  const term = getTermFromCard(card);
  return (
    (isTermCard(card) && term !== Facet.ReviewRate) ||
    isPostsCard(card) ||
    isGeoCard(card) ||
    isClassificationCard(card)
  ); //TODO:  add other card if necessary
}
export function isCardWithDisplayedValues(card: Card): boolean {
  const metric = getPrimaryMetricFromCard(card);
  const isCardAvailable =
    isTermCard(card) || isGeoCard(card) || isClassificationCard(card);
  //TODO:  add other card if necessary
  return isCardAvailable && metric !== Metric.Rating;
}

export function isUrlActionTile(card: Card): boolean {
  if (isEmbeddedCard(card)) {
    return card.dataviz.displayLink;
  }
  return null;
}

export function isFilterableTile(card: Card): boolean {
  if (
    isEmbeddedCard(card) ||
    isTextCard(card) ||
    isPinnedPostCard(card) ||
    isPinnedAuthorCard(card)
  ) {
    return null;
  }
  return true;
}

const MAX_FOCUSES = 60;
export function useCardLimits(card: Card, focuses: Query[]): string {
  const term = getTermFromCard(card);
  if (term === Facet.Focuses) {
    const clauseFocuses = getFocusesFromClause(getClauseFromCard(card));
    const clauseExcludedFocuses = getExcludedFocusesFromClause(
      getClauseFromCard(card)
    );
    if (
      clauseFocuses.length + clauseExcludedFocuses.length > MAX_FOCUSES ||
      (clauseFocuses.length === 0 &&
        focuses.length + clauseExcludedFocuses.length > MAX_FOCUSES)
    ) {
      return "MAX_FOCUSES_REACHED";
    }
  }

  return "";
}

export function cardWithoutPeriod(card: Card) {
  return (
    isPinnedPostCard(card) ||
    isPinnedAuthorCard(card) ||
    isTextCard(card) ||
    isEmbeddedCard(card)
  );
}

export function isTimeUnitCard(card: Card): card is TimeUnitCard {
  return isTimeUnitDataviz(card.dataviz);
}

export function isCardWithRequest(
  card: Card
): card is Exclude<Card, PinnedAuthorCard> {
  const fragment = card.fragments[0] as GlobalFragment;
  if (isInfluencerFragment(fragment)) {
    return false;
  }
  if (isAIFragment(fragment)) {
    return true;
  }
  return has(fragment, "request") && !isNil(fragment.request);
}
